import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useMemo, useState } from "react";
import { searchByProp } from "utils/search";
import { FETCH_COMPLIANCE_STANDARDS } from "./graphql";

function ComplianceStandardsSelector({
  onDone,
  defaultSelectedStandards = [],
}) {
  const [searchText, setSearchText] = useState("");
  const [selectedStandards, setSelectedStandards] = useState(
    defaultSelectedStandards,
  );
  const { loading, error, data } = useQuery(FETCH_COMPLIANCE_STANDARDS, {
    fetchPolicy: "cache-first",
  });

  const selectedIdSet = useMemo(
    () => new Set(selectedStandards.map((standard) => standard.id)),
    [selectedStandards],
  );

  function toggleStandard(standard) {
    if (selectedIdSet.has(standard.id)) {
      setSelectedStandards((prev) =>
        prev.filter((item) => item.id !== standard.id),
      );
      return;
    }
    setSelectedStandards((prev) => [...prev, standard]);
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="flex flex-col flex-1 max-h-[60vh]">
      <SearchBar
        bgColor="dark:bg-gray-900"
        autoFocus
        type="text"
        placeholder="Search by standard name"
        value={searchText}
        onChange={setSearchText}
      />

      <div className="flex-1 mt-4 overflow-auto">
        {(data?.complianceStandards || [])
          .filter((standard) => searchByProp(standard, ["name"], searchText))
          .map((standard) => (
            <div
              key={standard.id}
              className="flex items-center gap-3 border-b py-2 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"
              onClick={() => toggleStandard(standard)}
            >
              <input
                type="checkbox"
                checked={selectedIdSet.has(standard.id)}
                onChange={() => toggleStandard(standard)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex-1">{standard.name}</div>
            </div>
          ))}
      </div>

      <div className="pt-4 mt-2 border-t dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-xs opacity-70">
            {selectedStandards.length} standard
            {selectedStandards.length === 1 ? "" : "s"} selected
          </div>
          {selectedStandards.length > 0 ? (
            <Button
              danger
              className="text-xs"
              onClick={() => setSelectedStandards([])}
            >
              Clear Selection
            </Button>
          ) : null}
        </div>
        <Button primary onClick={() => onDone(selectedStandards)}>
          Done
        </Button>
      </div>
    </div>
  );
}

export default ComplianceStandardsSelector;
